import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import SortableTable from '../components/SortableTable';
import { Shield, Plus, Trash, PencilSimple, CheckCircle, XCircle, CaretDown, ArrowRight, FlowArrow } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = ['order', 'settlement', 'return', 'product'];
const OPERATORS = [
  { value: 'gte', label: '>= (greater or equal)' },
  { value: 'lte', label: '<= (less or equal)' },
  { value: 'eq', label: '= (equals)' },
];

function WorkflowForm({ workflow, onSave, onCancel }) {
  const [form, setForm] = useState(workflow || {
    name: '', entity_type: 'order', condition_field: 'total_amount',
    condition_operator: 'gte', condition_value: 10000, is_active: true,
    approval_levels: [{ level: 1, role: 'admin', label: 'Manager Approval' }]
  });

  const addLevel = () => {
    setForm(p => ({
      ...p, approval_levels: [...p.approval_levels, { level: p.approval_levels.length + 1, role: 'admin', label: '' }]
    }));
  };

  const removeLevel = (idx) => {
    setForm(p => ({
      ...p, approval_levels: p.approval_levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, level: i + 1 }))
    }));
  };

  const updateLevel = (idx, key, val) => {
    setForm(p => ({
      ...p, approval_levels: p.approval_levels.map((l, i) => i === idx ? { ...l, [key]: val } : l)
    }));
  };

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-6 mb-6" data-testid="workflow-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">Name</label>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="mt-1 w-full px-3 py-2 text-sm border border-[#e5e5ea] rounded-lg focus:border-[#7d64ff] outline-none"
            placeholder="e.g. High-Value Order Approval" data-testid="workflow-name-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">Entity Type</label>
          <select value={form.entity_type} onChange={e => setForm(p => ({ ...p, entity_type: e.target.value }))}
            className="mt-1 w-full px-3 py-2 text-sm border border-[#e5e5ea] rounded-lg focus:border-[#7d64ff] outline-none" data-testid="workflow-entity-select">
            {ENTITY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">Condition Field</label>
          <input value={form.condition_field} onChange={e => setForm(p => ({ ...p, condition_field: e.target.value }))}
            className="mt-1 w-full px-3 py-2 text-sm border border-[#e5e5ea] rounded-lg focus:border-[#7d64ff] outline-none"
            placeholder="e.g. total_amount" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">Operator</label>
          <select value={form.condition_operator} onChange={e => setForm(p => ({ ...p, condition_operator: e.target.value }))}
            className="mt-1 w-full px-3 py-2 text-sm border border-[#e5e5ea] rounded-lg focus:border-[#7d64ff] outline-none">
            {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">Threshold Value</label>
          <input type="number" value={form.condition_value} onChange={e => setForm(p => ({ ...p, condition_value: Number(e.target.value) }))}
            className="mt-1 w-full px-3 py-2 text-sm border border-[#e5e5ea] rounded-lg focus:border-[#7d64ff] outline-none" />
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#0A0A0A]">Approval Chain</h4>
          <button onClick={addLevel} className="text-xs font-semibold text-[#7d64ff] hover:text-[#6153a7] flex items-center gap-1" data-testid="add-approval-level">
            <Plus size={12} /> Add Level
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {form.approval_levels.map((level, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="bg-[#f8f7ff] border border-[#7d64ff]/20 rounded-lg px-3 py-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-[#7d64ff] text-white text-xs font-bold flex items-center justify-center">{level.level}</div>
                <input value={level.label} onChange={e => updateLevel(idx, 'label', e.target.value)}
                  className="w-32 text-xs border-none outline-none bg-transparent font-medium" placeholder="Approval label" />
                {form.approval_levels.length > 1 && (
                  <button onClick={() => removeLevel(idx)} className="text-[#ea4b5a] hover:text-red-700"><XCircle size={14} /></button>
                )}
              </div>
              {idx < form.approval_levels.length - 1 && <ArrowRight size={16} className="text-[#9c9db7]" />}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#f0f0f0]">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
          Active
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-[#5a5c87] hover:bg-[#f0f0f0] rounded-lg">Cancel</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 text-sm bg-[#7d64ff] text-white rounded-lg hover:bg-[#6153a7]" data-testid="save-workflow-btn">Save Workflow</button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalWorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWf, setEditingWf] = useState(null);
  const [tab, setTab] = useState('workflows');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [wfRes, qRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/approval-workflows`, { withCredentials: true }),
        axios.get(`${API_URL}/api/approval-queue?status=`, { withCredentials: true })
      ]);
      setWorkflows(wfRes.data);
      setQueue(qRes.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const saveWorkflow = async (form) => {
    try {
      if (editingWf) {
        await axios.patch(`${API_URL}/api/admin/approval-workflows/${editingWf.workflow_id}`, form, { withCredentials: true });
        toast.success('Workflow updated');
      } else {
        await axios.post(`${API_URL}/api/admin/approval-workflows`, form, { withCredentials: true });
        toast.success('Workflow created');
      }
      setShowForm(false);
      setEditingWf(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const deleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/approval-workflows/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleApproval = async (itemId, action) => {
    try {
      await axios.post(`${API_URL}/api/approval-queue/${itemId}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const wfColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'entity_type', label: 'Entity', sortable: true, render: (v) => <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#f2f0ff] text-[#7d64ff] capitalize">{v}</span> },
    { key: 'condition_field', label: 'Condition', render: (_, row) => <span className="text-xs font-mono">{row.condition_field} {row.condition_operator} {row.condition_value?.toLocaleString()}</span> },
    { key: 'approval_levels', label: 'Levels', render: (v) => <span className="text-xs font-bold">{v?.length || 0} step{(v?.length || 0) !== 1 ? 's' : ''}</span> },
    { key: 'is_active', label: 'Status', render: (v) => v ? <span className="text-xs font-bold text-[#28cd8c]">Active</span> : <span className="text-xs font-bold text-[#9c9db7]">Inactive</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div className="flex gap-1">
        <button onClick={() => { setEditingWf(row); setShowForm(true); }} className="p-1 text-[#7d64ff] hover:bg-[#f2f0ff] rounded" data-testid={`edit-wf-${row.workflow_id}`}><PencilSimple size={14} /></button>
        <button onClick={() => deleteWorkflow(row.workflow_id)} className="p-1 text-[#ea4b5a] hover:bg-red-50 rounded" data-testid={`del-wf-${row.workflow_id}`}><Trash size={14} /></button>
      </div>
    )}
  ];

  const queueColumns = [
    { key: 'entity_type', label: 'Type', sortable: true, render: (v) => <span className="capitalize font-semibold text-xs">{v}</span> },
    { key: 'entity_id', label: 'Entity ID', render: (v) => <span className="font-mono text-xs">{v}</span> },
    { key: 'summary', label: 'Summary' },
    { key: 'current_level', label: 'Level', render: (v, row) => <span className="text-xs">{v}/{row.max_levels}</span> },
    { key: 'status', label: 'Status', sortable: true, render: (v) => {
      const cls = v === 'pending' ? 'bg-[#f6a609]/10 text-[#d18f08]' : v === 'approved' ? 'bg-[#28cd8c]/10 text-[#28cd8c]' : 'bg-[#ea4b5a]/10 text-[#ea4b5a]';
      return <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${cls}`}>{v}</span>;
    }},
    { key: 'created_at', label: 'Requested', render: (v) => <span className="text-xs text-[#5a5c87]">{v ? new Date(v).toLocaleDateString() : '-'}</span> },
    { key: 'actions', label: '', render: (_, row) => row.status === 'pending' ? (
      <div className="flex gap-1">
        <button onClick={() => handleApproval(row.item_id, 'approve')} className="px-2 py-1 text-xs font-semibold bg-[#28cd8c] text-white rounded-lg hover:bg-[#22b87d]" data-testid={`approve-${row.item_id}`}>Approve</button>
        <button onClick={() => handleApproval(row.item_id, 'reject')} className="px-2 py-1 text-xs font-semibold bg-[#ea4b5a] text-white rounded-lg hover:bg-[#d63e4d]" data-testid={`reject-${row.item_id}`}>Reject</button>
      </div>
    ) : null }
  ];

  if (loading) return <DashboardLayout title="Approval Workflows"><div className="p-12 text-center"><div className="spinner mx-auto"></div></div></DashboardLayout>;

  return (
    <DashboardLayout title="Approval Workflows" subtitle="Configure multi-level approval chains" actions={
      <button onClick={() => { setEditingWf(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#7d64ff] text-white text-sm font-semibold rounded-lg hover:bg-[#6153a7]" data-testid="create-workflow-btn">
        <Plus size={16} /> New Workflow
      </button>
    }>
      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#f0f0f5] p-1 rounded-lg w-fit" data-testid="approval-tabs">
        {[{ id: 'workflows', label: 'Workflow Rules', icon: FlowArrow }, { id: 'queue', label: 'Approval Queue', icon: Shield }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${tab === t.id ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#5a5c87] hover:text-[#0A0A0A]'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={16} /> {t.label}
            {t.id === 'queue' && queue.filter(q => q.status === 'pending').length > 0 && (
              <span className="ml-1 w-5 h-5 bg-[#ea4b5a] text-white text-[10px] font-bold rounded-full flex items-center justify-center">{queue.filter(q => q.status === 'pending').length}</span>
            )}
          </button>
        ))}
      </div>

      {showForm && (
        <WorkflowForm workflow={editingWf} onSave={saveWorkflow} onCancel={() => { setShowForm(false); setEditingWf(null); }} />
      )}

      {tab === 'workflows' && (
        <SortableTable columns={wfColumns} data={workflows} emptyMessage="No approval workflows configured" pageSize={10} />
      )}

      {tab === 'queue' && (
        <SortableTable columns={queueColumns} data={queue} emptyMessage="No items pending approval" pageSize={15} />
      )}
    </DashboardLayout>
  );
}
